import React, {
  HTMLInputTypeAttribute,
  useCallback,
  useRef,
  useState,
} from "react";

import { IInputDialogProps } from "./IInputDialogProps";
import { Icon } from "@snpjs/core";
import styles from "./InputDialog.module.scss";
import useOnClickOutside from "../../hooks/useOnClickOutside";

export function InputDialog<T>(props: IInputDialogProps<T>) {
  const [inputValue, setInputValue] = useState(props.initialValue);
  const [error, setError] = useState<Error | undefined>(undefined);
  const inputDialogRef = useRef(null);

  const onClickOutside = useCallback(() => props.onCancel?.(), [props]);
  useOnClickOutside(inputDialogRef, onClickOutside);

  const onKeyDown = () => {
    return (event: React.KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case "Enter":
          onSubmit();
          break;
        case "Escape":
          props.onCancel?.();
          break;
      }
    };
  };

  const onSubmit = () => {
    try {
      props.validate?.(inputValue);
      props.onOk?.(inputValue);
    } catch (e) {
      setError(e as Error);
    }
  };

  const deriveType = (): HTMLInputTypeAttribute => {
    switch (typeof props.initialValue) {
      case "string":
        return "text";
      case "number":
        return "number";
      case "bigint":
        return "number";
      case "boolean":
        return "text";
      default:
        throw new Error("Not supported");
    }
  };

  const style = props.widthInRem ? { width: `${props.widthInRem}rem` } : {};

  return (
    <div className={styles.editInputDialog} ref={inputDialogRef}>
      <div className={styles.inputWithButtons}>
        <input
          value={inputValue as unknown as string}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setInputValue(event.target.value as unknown as T)
          }
          type={deriveType()}
          onKeyDown={onKeyDown()}
          className={`${styles.input} ${error && styles.validationError}`}
          autoFocus={props.autoFocus}
          style={style}
        />
        <Icon icon="check" onClick={onSubmit} className={styles.okButton} />
        <Icon
          icon="x"
          onClick={() => props.onCancel?.()}
          className={styles.cancelButton}
        ></Icon>
      </div>
      {error && (
        <span className={styles.validationErrorMessage}>{error.message}</span>
      )}
    </div>
  );
}
